import React from 'react';
import { Avatar, EntityCard, TagBadge, Text } from '@chatsdet/ui';
import { LinkSimple } from '@phosphor-icons/react';
import { APPLICATION_TYPE_LABELS, type ApplicationType } from '@chatsdet/types';

export type PropertyCardItem = {
  id: string;
  name: string;
  type: ApplicationType;
  /**
   * Property URL sourced from the property's default environment join.
   * Null when no default environment exists yet (newly created property
   * mid-setup, or a data-integrity edge case).
   */
  baseUrl: string | null;
  description?: string | null;
};

/**
 * Safely extract domain from URL and construct Google favicon service URL.
 * Returns empty string if URL is null or parsing fails.
 */
function getFaviconUrl(baseUrl: string | null): string {
  if (!baseUrl) return '';
  try {
    const url = new URL(baseUrl);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
  } catch {
    return '';
  }
}

/** Extract a display-friendly hostname from a URL. */
function getDisplayUrl(baseUrl: string | null): string {
  if (!baseUrl) return '—';
  try {
    const url = new URL(baseUrl);
    return url.hostname;
  } catch {
    return baseUrl;
  }
}

/**
 * Avatar that only shows favicon if it's high enough resolution.
 * Falls back to initials if image is too small (likely a default icon).
 */
function FaviconAvatar({ baseUrl, name }: { baseUrl: string | null; name: string }) {
  const [validFavicon, setValidFavicon] = React.useState<string | undefined>(undefined);
  const faviconUrl = getFaviconUrl(baseUrl);

  React.useEffect(() => {
    if (!faviconUrl) {
      setValidFavicon(undefined);
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth >= 20 && img.naturalHeight >= 20) {
        setValidFavicon(faviconUrl);
      } else {
        setValidFavicon(undefined);
      }
    };
    img.onerror = () => {
      setValidFavicon(undefined);
    };
    img.src = faviconUrl;
  }, [faviconUrl]);

  return <Avatar src={validFavicon} name={name} size="md" className="rounded-lg" />;
}

type PropertyCardProps = {
  property: PropertyCardItem;
  onView: (id: string) => void;
};

export function PropertyCard({ property, onView }: PropertyCardProps) {
  return (
    <EntityCard
      avatar={<FaviconAvatar baseUrl={property.baseUrl} name={property.name} />}
      title={property.name}
      status={<TagBadge name={APPLICATION_TYPE_LABELS[property.type]} color="gray" size="sm" hideDot />}
      meta={
        <span className="flex items-center gap-1.5">
          <LinkSimple size={13} className="shrink-0" />
          <span className="truncate">{getDisplayUrl(property.baseUrl)}</span>
        </span>
      }
      description={
        property.description ? (
          <Text size="sm" className="text-muted-foreground line-clamp-2">
            {property.description}
          </Text>
        ) : undefined
      }
      actionLabel="View"
      onPress={() => onView(property.id)}
    />
  );
}
